
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Getter } from 'vuex-class';

import { OrderMethods } from '@/enums';
import BasketModule from '@/store/modules/basket';
import { addHoursToDate, dayToIndexMap, formatHour, hourMethodMap, indexToDayMap, parseDate, roundUp } from '@/utils/global.util';

@Component({
  components: {
  },
})
export default class TimeWanted extends Vue {
  @Getter activeRestaurant!: Restaurant;
  @Prop({ required: true }) currentOrderMethod: OrderMethods | null;
  @Prop({ type: Boolean, required: true }) isEditMode: boolean;

  timeWantedDays: SelectorItem[];
  timeWantedTimes: TimeWantedTimes;
  timeWantedIncrement: number;
  timeWantedFormModel: TimeWantedFormModel;

  data() {
    return {
      timeWantedIncrement: 15,
      timeWantedFormModel: {
        date: '',
        time: '',
      },
    }
  }

  @Watch('showTimeWantedContainer')
  watchShowTimeWanted() {
    const timeMode = BasketModule.current?.timeMode;
    const timeWanted = BasketModule.current?.timeWanted;
    if (timeMode === 'advance' && timeWanted) {
      const [date, time] = timeWanted?.split(' ') || [];
      const year = date.substring(0, 4);
      const month = date.substring(4, 6);
      const day = date.substring(6, 8);
      const useDate = `${year}-${month}-${day}`;
      this.timeWantedFormModel.date = useDate;
      this.timeWantedFormModel.time = time;
    }
  }

  get timeWantedTime() {
    const firstDay = this.timeWantedDay?.[0]?.value;
    const dateString = this.timeWantedFormModel?.date || firstDay;
    let times = this.timeWantedTimes?.[dateString] || this.getTimeWantedTimes(this.timeWantedDay, this.activeRestaurant)?.[dateString] || [];

    if (dateString === firstDay) {
      const readyHour = this.firstAvailableTime.getHours();
      const readyMinutes = this.firstAvailableTime.getMinutes();
      times = times.filter((time) => {
        const [hour, min] = time.value.split(':')?.map((i) => parseInt(i, 10));
        const roundedReadyMinutes = roundUp(readyMinutes, this.timeWantedIncrement);

        const hourIsInFuture = hour > readyHour;
        const isCurrentHour = hour === readyHour;
        const timeIsInFuture = min >= roundedReadyMinutes;
        const isASAP = time.value === 'ASAP';

        return ((hourIsInFuture || isCurrentHour) && timeIsInFuture) || hourIsInFuture || isASAP;
      })
    }
    return times;
  }

  get timeWantedDay() {
    return this.timeWantedDays || this.getTimeWantedDays(this.activeRestaurant) || [];
  }

  get today(): Date {
    // return new Date('2021-09-11T12:10:00'); // used to spoof times for testing
    return new Date();
  }

  get tomorrow(): Date {
    return addHoursToDate(this.today, 24);
  }

  get parsedReadyTime() {
    // return new Date('2021-09-11T12:28:00'); //  used to spoof times for testing
    const { earliestReadyTime } = BasketModule?.current || { earliestReadyTime: new Date() };
    return parseDate(earliestReadyTime);
  }

  get firstAvailableTime(): Date {
    const openUntil = this.getOpenUntilHours(this.hoursForTodayAndMethod(this.today.getDay()));
    // location is now closed for today
    // TODO~> this fails if hour ends in 60... fix that
    if (openUntil.hour <= this.today.getHours() && (openUntil.mins <= this.today.getMinutes() || this.today.getMinutes() === 0)) {
      const openFrom = this.getOpenFromHours(this.hoursForTodayAndMethod(this.tomorrow.getDay()));
      const tomorrow = new Date(new Date(new Date(new Date().setDate(this.tomorrow.getDate())).setHours(openFrom.hour)).setMinutes(openFrom.mins));
      return tomorrow;
    } else {
      // location is open and ERT is in the future
      if (this.parsedReadyTime > this.today) {
        return this.parsedReadyTime;
      }
      // location is open and ERT is in the past
      return this.today;
    }
  }

  formatDateOrMonth(value) {
    return value <= 9 ? `0${value}` : value;
  }

  get todayValue(): string {
    const date = this.today.getDate();
    const formattedDate = this.formatDateOrMonth(date);
    const month = this.today.getMonth() + 1;
    const formattedMonth = this.formatDateOrMonth(month);
    return `${this.today.getFullYear()}-${formattedMonth}-${formattedDate}`;
  }

  get tomorrowValue(): string {
    const date = this.tomorrow.getDate();
    const formattedDate = this.formatDateOrMonth(date);
    const month = this.today.getMonth() + 1;
    const formattedMonth = this.formatDateOrMonth(month);
    return `${this.tomorrow.getFullYear()}-${formattedMonth}-${formattedDate}`;
  }

  get showTimeWantedContainer() {
    return (((this.currentOrderMethod === OrderMethods.PICKUP && BasketModule.id !== '') ||
      (this.currentOrderMethod === OrderMethods.CURBSIDE && BasketModule.curbsideDetailsExist) ||
      (this.currentOrderMethod === OrderMethods.DELIVERY && (BasketModule.current?.deliveryAddress !== null && BasketModule.current?.deliveryAddress !== undefined))) && !this.isEditMode);
  }

  get deliveryMode() {
    return BasketModule.deliveryMode;
  }

  @Watch('activeRestaurant', { immediate: false })
  watchActiveRestaurant(newActiveRestaurant, oldVal) {
    const restaurantExists = newActiveRestaurant?.id;
    const itsDifferentFromLastTime = newActiveRestaurant?.id !== oldVal?.id;
    const hasHours = newActiveRestaurant?.hours?.length;
    if (restaurantExists && itsDifferentFromLastTime && hasHours) {
      const days = this.getTimeWantedDays(newActiveRestaurant);
      const times = this.getTimeWantedTimes(days, newActiveRestaurant);

      const firstDay = days[0]?.value as string;
      this.timeWantedFormModel.date = firstDay;
      this.timeWantedFormModel.time = times[firstDay]?.[0]?.value;

      this.timeWantedDays = days;
      this.timeWantedTimes = times;
    }
  }

  @Watch('timeWantedFormModel', { immediate: false })
  watchTimeWantedFormModel(newVal, oldVal) {
    const notToday = newVal?.date !== this.todayValue;
    const todayIsSelected = newVal?.date === this.todayValue;
    const todayWasSelected = oldVal.date === this.todayValue;
    const ASAPIsSelected = newVal.time === 'ASAP';
    const tomorrowWasSelected = newVal?.date === this.tomorrowValue && newVal?.date !== oldVal?.date;
    const hasTimeWantedTimes = this.timeWantedTime?.length;

    const datehasTime = this.timeWantedTime?.find((twt) => twt.value === newVal.time);

    if (
      notToday &&
      todayWasSelected &&
      ASAPIsSelected &&
      hasTimeWantedTimes
    ) {
      // selected date that is not today
      this.timeWantedFormModel.time = this.timeWantedTime?.[0]?.value;
    } else if (todayIsSelected && !datehasTime) {
      // Today was selected
      this.timeWantedFormModel.time = 'ASAP';
    } else if (todayWasSelected && tomorrowWasSelected && hasTimeWantedTimes) {
      this.timeWantedFormModel.time = this.timeWantedTime?.[0]?.value;
    }
  }

  hoursForTodayAndMethod(dayInt: number): Period {
    const hours = this.activeRestaurant?.hours?.find((hour) => {
      const methodMatch = hour.type === hourMethodMap.get(this.deliveryMode as string);
      const dayMatch = hour.day === indexToDayMap.get(dayInt);
      return methodMatch && dayMatch;
    }) as Period;

    return hours;
  }

  getOpenFromHours(hoursForDayAndMethod): { hour: number; mins: number } {
    const [hour, mins] = hoursForDayAndMethod?.from?.split(':')?.map((i) => parseInt(i, 10)) || [];
    return { hour, mins };
  }

  getOpenUntilHours(hoursForDayAndMethod): { hour: number; mins: number } {
    const [hour, mins] = hoursForDayAndMethod?.to?.split(':')?.map((i) => parseInt(i, 10)) || [];
    return { hour, mins };
  }

  getTimeWantedDays(activeRestaurant: Restaurant) {
    const days: SelectorItem[] = [];
    const extraMonth = 1;
    try {
      const { advanceOrderDays = 14 } = activeRestaurant || {};
      for (let count = 0; count < advanceOrderDays; ++count) {
        const dateItem = count > 0 ? addHoursToDate(this.firstAvailableTime, 24 * count) : this.firstAvailableTime;

        const hasDailyHours = activeRestaurant?.hours?.some((hour) => dayToIndexMap.get(hour.day) === dateItem.getDay())
        if (!hasDailyHours) { continue; }
        const date = dateItem.getDate();
        const month = dateItem.getMonth() + extraMonth;
        const formattedDate = this.formatDateOrMonth(date);
        const formattedMonth = this.formatDateOrMonth(month);
        const value = `${dateItem.getFullYear()}-${formattedMonth}-${formattedDate}`;
        const label = `${indexToDayMap.get(dateItem.getDay())} ${month}/${formattedDate}`;
        const isToday = this.todayValue === value;
        const isTomorrow = this.tomorrowValue === value;
        const day = {
          value,
          id: value,
          label: isToday ? 'Today' : isTomorrow ? 'Tomorrow' : label,
        };

        days.push(day);
      }
    } catch (err) {
      this.$log.error(err);
    }
    return days;
  }

  getTimeWantedTime(day: SelectorItem, activeRestaurant: Restaurant) {
    const minutes: SelectorItem[] = [];
    try {
      const asapOrderCutOffMinutesBeforeClosing = activeRestaurant.asapOrderCutOffMinutesBeforeClosing || 15;

      const thisDate = new Date(day.value + 'T00:00:00');
      const openFrom = this.getOpenFromHours(this.hoursForTodayAndMethod(thisDate.getDay()));
      const openUntil = this.getOpenUntilHours(this.hoursForTodayAndMethod(thisDate.getDay()));

      for (let hour = openFrom.hour; hour < openUntil.hour; ++hour) {
        const sixty = 60;
        const untilMins = hour === (openUntil.hour - 1) ? sixty - asapOrderCutOffMinutesBeforeClosing : sixty;

        for (let mins = openFrom.mins; mins <= untilMins; mins = mins + this.timeWantedIncrement) {
          const hourValue = mins === sixty ? hour + 1 : hour;
          const minValue = (mins === sixty || mins === 0) ? '00' : mins;
          const value = `${this.formatDateOrMonth(hourValue)}:${minValue}`;

          const min = {
            value,
            id: formatHour(value),
            label: formatHour(value),
          };

          const existingEntry = minutes.find((minute) => minute.id === min.id);
          if (!existingEntry) {
            minutes.push(min);
          }
        }
      }
      // if date is today
      if (day.label === 'Today') {
        minutes.unshift({ value: 'ASAP', label: 'ASAP', id: 'ASAP' });
      }
    } catch (err) {
      this.$log.error(err);
    }
    return minutes;
  }

  getTimeWantedTimes(days: SelectorItem[], activeRestaurant: Restaurant): TimeWantedTimes {
    const timesForDay = (days || []).reduce((acc, twd) => {
      const temp = this.getTimeWantedTime(twd, activeRestaurant);
      acc[twd.value] = temp;
      return acc;
    }, {});
    return timesForDay;
  }

  async submitTimeWanted(data) {
    const loader = this.$loading.show();
    try {
      const { time, date } = data;
      if (time !== 'ASAP') {
        const [year, month, day] = date?.split('-')?.map((i) => parseInt(i, 10)) || [];
        const [hour, minute] = time?.split(':')?.map((i) => parseInt(i, 10)) || [];
        const timeWanted: TimeWantedRequest = {
          year,
          month,
          day,
          hour,
          minute,
        };
        await BasketModule.setTimeWanted(timeWanted);
      } else if (BasketModule.current && BasketModule.current.timeMode.toLowerCase() !== 'asap') {
        await BasketModule.setTimeWanted({} as TimeWantedRequest);
      }

      this.$emit('on-save');
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    } finally {
      loader.hide();
    }
  }
}
